<template>
  <div class="date-range-picker">
    <b-input-group>
      <b-input-group-prepend>
        <b-input-group-text>
          <i class="fas fa-calendar-alt"></i>
        </b-input-group-text>
      </b-input-group-prepend>
      <b-form-input
        :value="displayValue"
        readonly
        placeholder="Selecione o período"
        @click="togglePicker"
      ></b-form-input>
      <b-input-group-append v-if="hasValue">
        <b-button variant="outline-secondary" @click="clearDates">
          <i class="fas fa-times"></i>
        </b-button>
      </b-input-group-append>
    </b-input-group>

    <div v-if="showPicker" class="calendar-dropdown">
      <div class="calendar-header">
        <b-button variant="link" size="sm" @click="previousMonth">
          <i class="fas fa-chevron-left"></i>
        </b-button>
        <span class="month-year">{{ currentMonthYear }}</span>
        <b-button variant="link" size="sm" @click="nextMonth">
          <i class="fas fa-chevron-right"></i>
        </b-button>
      </div>

      <div class="calendar-body">
        <div class="weekdays">
          <div v-for="day in weekDays" :key="day" class="weekday">
            {{ day }}
          </div>
        </div>

        <div class="days">
          <div
            v-for="(day, index) in calendarDays"
            :key="index"
            :class="getDayClass(day)"
            @click="selectDate(day)"
          >
            {{ day ? day.date() : "" }}
          </div>
        </div>
      </div>

      <div class="calendar-footer">
        <b-button size="sm" variant="link" @click="clearDates">
          Limpar
        </b-button>
        <b-button size="sm" variant="primary" @click="applyDates">
          Aplicar
        </b-button>
      </div>
    </div>

    <div v-if="showPicker" class="calendar-overlay" @click="closePicker"></div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "DateRangePicker",
  props: {
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showPicker: false,
      currentMonth: moment(),
      tempStartDate: null,
      tempEndDate: null,
      weekDays: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
    };
  },
  computed: {
    hasValue() {
      return this.startDate || this.endDate;
    },
    displayValue() {
      if (this.startDate && this.endDate) {
        return `${moment(this.startDate).format("DD/MM/YYYY")} - ${moment(
          this.endDate
        ).format("DD/MM/YYYY")}`;
      }
      if (this.startDate) {
        return `${moment(this.startDate).format("DD/MM/YYYY")} - ...`;
      }
      return "";
    },
    currentMonthYear() {
      return this.currentMonth.format("MMMM YYYY");
    },
    calendarDays() {
      const startOfMonth = this.currentMonth.clone().startOf("month");
      const endOfMonth = this.currentMonth.clone().endOf("month");
      const startDate = startOfMonth.clone().startOf("week");
      const endDate = endOfMonth.clone().endOf("week");

      const days = [];
      const day = startDate.clone();

      while (day.isBefore(endDate, "day")) {
        if (day.month() === this.currentMonth.month()) {
          days.push(day.clone());
        } else {
          days.push(null);
        }
        day.add(1, "day");
      }

      return days;
    },
  },
  watch: {
    startDate(newVal) {
      this.tempStartDate = newVal ? moment(newVal) : null;
    },
    endDate(newVal) {
      this.tempEndDate = newVal ? moment(newVal) : null;
    },
  },
  mounted() {
    this.tempStartDate = this.startDate ? moment(this.startDate) : null;
    this.tempEndDate = this.endDate ? moment(this.endDate) : null;
  },
  methods: {
    togglePicker() {
      this.showPicker = !this.showPicker;
    },
    closePicker() {
      this.showPicker = false;
    },
    previousMonth() {
      this.currentMonth = this.currentMonth.clone().subtract(1, "month");
    },
    nextMonth() {
      this.currentMonth = this.currentMonth.clone().add(1, "month");
    },
    selectDate(day) {
      if (!day) return;

      if (!this.tempStartDate || (this.tempStartDate && this.tempEndDate)) {
        // Primeira seleção ou começar novo intervalo
        this.tempStartDate = day.clone();
        this.tempEndDate = null;
      } else {
        // Segunda seleção
        if (day.isBefore(this.tempStartDate)) {
          this.tempEndDate = this.tempStartDate.clone();
          this.tempStartDate = day.clone();
        } else {
          this.tempEndDate = day.clone();
        }
      }
    },
    getDayClass(day) {
      if (!day) return "day empty";

      const classes = ["day"];
      const dayStr = day.format("YYYY-MM-DD");

      // Verifica se é hoje
      if (day.isSame(moment(), "day")) {
        classes.push("today");
      }

      // Verifica se é data de início
      if (this.tempStartDate && day.isSame(this.tempStartDate, "day")) {
        classes.push("selected start");
      }

      // Verifica se é data de fim
      if (this.tempEndDate && day.isSame(this.tempEndDate, "day")) {
        classes.push("selected end");
      }

      // Verifica se está no intervalo
      if (this.tempStartDate && this.tempEndDate) {
        if (
          day.isAfter(this.tempStartDate, "day") &&
          day.isBefore(this.tempEndDate, "day")
        ) {
          classes.push("in-range");
        }
      }

      // Hover effect quando só tem data inicial
      if (
        this.tempStartDate &&
        !this.tempEndDate &&
        day.isAfter(this.tempStartDate, "day")
      ) {
        classes.push("hover-range");
      }

      return classes.join(" ");
    },
    applyDates() {
      const startDate = this.tempStartDate
        ? this.tempStartDate.format("YYYY-MM-DD")
        : null;
      const endDate = this.tempEndDate
        ? this.tempEndDate.format("YYYY-MM-DD")
        : null;

      this.$emit("update:startDate", startDate);
      this.$emit("update:endDate", endDate);
      this.$emit("change");
      this.closePicker();
    },
    clearDates() {
      this.tempStartDate = null;
      this.tempEndDate = null;
      this.$emit("update:startDate", null);
      this.$emit("update:endDate", null);
      this.$emit("change");
      this.closePicker();
    },
  },
};
</script>

<style scoped>
.date-range-picker {
  position: relative;
}

.date-range-picker .form-control {
  cursor: pointer;
  background-color: #fff;
}

.calendar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
}

.calendar-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  z-index: 1050;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  min-width: 300px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
}

.calendar-header .btn-link {
  color: #495057;
  text-decoration: none;
  padding: 0.25rem 0.5rem;
}

.calendar-header .btn-link:hover {
  color: #007bff;
}

.month-year {
  font-weight: 600;
  font-size: 1rem;
  color: #495057;
  text-transform: capitalize;
}

.calendar-body {
  margin-bottom: 1rem;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.weekday {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  padding: 0.25rem;
}

.days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  color: #495057;
}

.day.empty {
  cursor: default;
}

.day:not(.empty):hover {
  background-color: #e9ecef;
}

.day.today {
  font-weight: 600;
  color: #007bff;
}

.day.selected {
  background-color: #007bff;
  color: white;
  font-weight: 600;
}

.day.selected.start {
  border-radius: 6px 0 0 6px;
}

.day.selected.end {
  border-radius: 0 6px 6px 0;
}

.day.selected.start.end {
  border-radius: 6px;
}

.day.in-range {
  background-color: rgba(0, 123, 255, 0.2);
  color: #007bff;
}

.day.hover-range {
  background-color: rgba(0, 123, 255, 0.1);
}

.calendar-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
}

.calendar-footer .btn-link {
  color: #6c757d;
  text-decoration: none;
}

.calendar-footer .btn-link:hover {
  color: #495057;
}

::v-deep .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
}
</style>
